import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ActiveTour, TourStep } from '@/lib/tour/types';

type TourState = {
  /** Currently active tour, or null */
  activeTour: ActiveTour | null;
  /** Set of tour IDs the user has completed */
  completedTours: string[];
  /** Set of tour IDs the user has explicitly dismissed */
  dismissedTours: string[];

  /** Start a tour with the given (pre-filtered) steps */
  startTour: (tourId: string, steps: TourStep[]) => void;
  /** Move to the next step. Returns false if tour ended. */
  nextStep: () => boolean;
  /** Move to the previous step */
  prevStep: () => void;
  /** Jump to a specific step */
  goToStep: (step: number) => void;
  /** Complete and close the active tour */
  completeTour: () => void;
  /** Dismiss (skip) the active tour without marking it completed */
  dismissTour: () => void;
  /** Reset a specific tour so it can be shown again */
  resetTour: (tourId: string) => void;
  /** Check if a tour has been completed */
  isCompleted: (tourId: string) => boolean;
  /** Check if a tour has been dismissed */
  isDismissed: (tourId: string) => boolean;
};

export const useTourStore = create<TourState>()(
  persist(
    (set, get) => ({
      activeTour: null,
      completedTours: [],
      dismissedTours: [],

      startTour: (tourId, steps) => {
        set({ activeTour: { tourId, currentStep: 0, steps } });
      },

      nextStep: () => {
        const { activeTour } = get();
        if (!activeTour) return false;
        const next = activeTour.currentStep + 1;
        if (next >= activeTour.steps.length) {
          get().completeTour();
          return false;
        }
        set({ activeTour: { ...activeTour, currentStep: next } });
        return true;
      },

      prevStep: () => {
        const { activeTour } = get();
        if (!activeTour || activeTour.currentStep <= 0) return;
        set({ activeTour: { ...activeTour, currentStep: activeTour.currentStep - 1 } });
      },

      goToStep: (step) => {
        const { activeTour } = get();
        if (!activeTour) return;
        set({ activeTour: { ...activeTour, currentStep: step } });
      },

      completeTour: () => {
        const { activeTour, completedTours } = get();
        if (!activeTour) return;
        const updated = completedTours.includes(activeTour.tourId)
          ? completedTours
          : [...completedTours, activeTour.tourId];
        set({ activeTour: null, completedTours: updated });
      },

      dismissTour: () => {
        const { activeTour, dismissedTours } = get();
        if (!activeTour) return;
        const updated = dismissedTours.includes(activeTour.tourId)
          ? dismissedTours
          : [...dismissedTours, activeTour.tourId];
        set({ activeTour: null, dismissedTours: updated });
      },

      resetTour: (tourId) => {
        set({
          completedTours: get().completedTours.filter((id) => id !== tourId),
          dismissedTours: get().dismissedTours.filter((id) => id !== tourId),
        });
      },

      isCompleted: (tourId) => get().completedTours.includes(tourId),
      isDismissed: (tourId) => get().dismissedTours.includes(tourId),
    }),
    {
      name: 'solidafy-tour-store',
      // version 1: excludes activeTour from persistence (steps contain non-serializable
      // functions). Old persisted activeTour state (version 0) is discarded on hydration.
      version: 1,
      // Don't persist the activeTour — steps array contains functions (onActivate)
      // which can't be serialized. A refreshed session will re-filter steps anyway.
      partialize: (state) => ({
        completedTours: state.completedTours,
        dismissedTours: state.dismissedTours,
      }),
    },
  ),
);
